import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth-store';
import { Loader2, Check } from 'lucide-react';

interface TwoFactorData {
  qr_code_url: string;
  secret_key: string;
  manual_entry_key: string;
  is_enabled: boolean;
  app_name: string;
  user_email: string;
}

export default function TwoFactorPage() {
  const navigate = useNavigate();
  const { token, user, setUser } = useAuthStore();
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnabling, setIsEnabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadTwoFactorData();
  }, []);

  const loadTwoFactorData = async () => {
    if (!token) {
      setError('Authentication token not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authApi.getTwoFactorQrCode(token);
      
      if (response.success) {
        setTwoFactorData(response.data);
      } else {
        setError(response.message || 'Failed to load 2FA setup data');
      }
    } catch (err) {
      setError('Network error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySecret = async () => {
    const keyToCopy = twoFactorData?.manual_entry_key || twoFactorData?.secret_key;
    if (keyToCopy) {
      try {
        await navigator.clipboard.writeText(keyToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = keyToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (!twoFactorData) {
      setError('2FA setup data not available');
      return;
    }

    if (!token) {
      setError('Authentication token not found');
      return;
    }

    try {
      setIsEnabling(true);
      setError(null);
      
      const response = await authApi.enableTwoFactor(token, {
        secret_key: twoFactorData.secret_key,
        verification_code: verificationCode
      });
      
      if (response.success) {
        // Update the user in the store so the settings page reflects the change immediately
        if (user) {
          setUser({ ...user, google_auth_enabled: true });
        }
        navigate('/settings');
      } else {
        setError(response.message || 'Failed to enable 2FA');
      }
    } catch (err) {
      setError('Network error occurred. Please try again.');
    } finally {
      setIsEnabling(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Loading 2FA setup...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !twoFactorData) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center">
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
            <button
              onClick={loadTwoFactorData}
              className="bg-yellow-400 text-black px-4 py-2 rounded-lg hover:bg-yellow-500 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/img/logo.png" 
              alt="LogisHaus" 
              className="h-12 mr-3"
            />
            <span className="text-2xl font-bold text-gray-900">LogisHaus</span>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900">Two - factor authentication</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {twoFactorData && (
          <>
            {/* QR Code */}
            <div className="mb-6 text-center">
              <div className="inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${twoFactorData.qr_code_url}&margin=10&format=png`}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://chart.googleapis.com/chart?chs=200x200&chld=M|0&cht=qr&chl=${encodeURIComponent(twoFactorData.qr_code_url)}`;
                  }}
                />
              </div>
            </div>

            {/* Instructions Text */}
            <div className="mb-6 text-center">
              <p className="text-sm text-gray-600">
                "Scan the QR Code using any authentication application on your phone (e.g. <a href="https://support.google.com/accounts/answer/1066447" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Google Authenticator</a>, <a href="https://duo.com/product/trusted-users/two-factor-authentication/duo-mobile" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Duo Mobile</a>, <a href="https://authy.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Authy</a>) or enter the following code:
              </p>
            </div>

            {/* Manual Entry Key */}
            <div className="mb-6 text-center">
              <div className="flex items-center justify-center space-x-2">
                <span className="text-sm font-mono text-gray-800 select-all">
                  {twoFactorData.manual_entry_key || twoFactorData.secret_key || 'No code available'}
                </span>
                <button
                  onClick={handleCopySecret}
                  className="p-1 transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <img src="/img/ion_copy-sharp.svg" alt="Copy" className="w-5 h-5" />
                  )}
                </button>
              </div>
              {copied && (
                <p className="text-xs text-green-600 mt-1">Copied to clipboard!</p>
              )}
            </div>

            {/* Verification Code Input */}
            <div className="mb-6 text-center">
              <label htmlFor="verificationCode" className="block text-sm text-gray-600 mb-3">
                Enter the 6-digit code from your authenticator app:
              </label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                placeholder="000000"
                maxLength={6}
              />
            </div>

            {/* Separator */}
            <hr className="border-gray-200 mb-6" />

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                disabled={isEnabling}
                className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 focus:outline-none transition-colors disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                onClick={handleActivate}
                disabled={isEnabling || verificationCode.length !== 6}
                className="flex-1 bg-yellow-400 text-black py-3 px-4 rounded-lg font-medium hover:bg-yellow-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEnabling ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Activating...
                  </div>
                ) : (
                  'Activate'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
